import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { catchError, of } from 'rxjs';
import { Company } from '../../models/company.model';
import { CompanyService } from '../../services/company.service';
import { AuthService } from '../../services/auth.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-company-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    InputText,
    Select,
    Tooltip,
    ConfirmDialog
  ],
  templateUrl: './company-management.component.html',
  styleUrl: './company-management.component.scss',
  providers: [ConfirmationService]
})
export class CompanyManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  companies: Company[] = [];
  filteredCompanies: Company[] = [];
  selectedCompany: Company = this.getEmptyCompany();
  displayDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  loading = false;
  searchText: string = '';
  selectedStatusFilter: string | null = 'active';
  fieldTouched: Record<string, boolean> = {};

  rows: number = 50;
  first: number = 0;
  totalRecords: number = 0;
  rowsPerPageOptions: number[] = [5, 10, 20, 50];

  statusFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  get totalCompaniesCount(): number { return this.companies.length; }
  get activeCompaniesCount(): number { return this.companies.filter(c => c.isActive).length; }
  get inactiveCompaniesCount(): number { return this.companies.filter(c => !c.isActive).length; }

  constructor(
    private companyService: CompanyService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by company name...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Company');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadCompanies();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applySearch();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.showAddDialog());
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.exportToExcel());
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.pageHeaderService.setCanShowPrimaryAction(!!user?.isAdmin);
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadCompanies() {
    this.loading = true;
    this.companyService.getAllCompanies().pipe(
      catchError(error => {
        console.error('Error loading companies:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load companies. Please refresh the page or try again later.'
        });
        this.loading = false;
        return of([] as Company[]);
      })
    ).subscribe({
      next: (data) => {
        this.companies = data.map(c => ({
          ...c,
          isActive: c.isActive !== undefined ? c.isActive : true
        }));
        this.applySearch();
        this.loading = false;
      }
    });
  }

  onSearch() {
    this.applySearch();
  }

  applySearch() {
    let filtered = [...this.companies];

    // Apply search text filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (this.selectedStatusFilter !== null) {
      if (this.selectedStatusFilter === 'active') {
        filtered = filtered.filter(c => c.isActive === true);
      } else if (this.selectedStatusFilter === 'inactive') {
        filtered = filtered.filter(c => c.isActive === false);
      }
    }

    this.filteredCompanies = filtered;
    this.totalRecords = filtered.length;
    this.first = 0;
    this.pageHeaderService.setExportDisabled(this.filteredCompanies.length === 0);
  }

  onStatusFilterChange() {
    this.applySearch();
  }

  clearSearch() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.applySearch();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText.trim() || (this.selectedStatusFilter != null && this.selectedStatusFilter !== 'active'));
  }

  clearFilters() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatusFilter = 'active';
    this.applySearch();
  }

  showAddDialog() {
    this.selectedCompany = this.getEmptyCompany();
    this.fieldTouched = {};
    this.dialogMode = 'add';
    this.displayDialog = true;
  }

  showEditDialog(company: Company) {
    this.selectedCompany = { ...company };
    this.fieldTouched = {};
    this.dialogMode = 'edit';
    this.displayDialog = true;
  }

  saveCompany() {
    this.fieldTouched['name'] = true;
    if (!this.selectedCompany.name.trim()) {
      return;
    }

    this.loading = true;

    if (this.dialogMode === 'add') {
      this.companyService.createCompany(this.selectedCompany).pipe(
        catchError(error => {
          console.error('Error creating company:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create company. Please try again.'
          });
          this.loading = false;
          return of(null);
        })
      ).subscribe({
        next: (result) => {
          if (result) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Company created successfully.'
            });
            this.hideDialog();
            this.loadCompanies();
          }
        }
      });
    } else {
      this.companyService.updateCompany(this.selectedCompany.id!, this.selectedCompany).pipe(
        catchError(error => {
          console.error('Error updating company:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update company. Please try again.'
          });
          this.loading = false;
          return of(null);
        })
      ).subscribe({
        next: (result) => {
          if (result) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Company updated successfully.'
            });
            this.hideDialog();
            this.loadCompanies();
          }
        }
      });
    }
  }

  toggleActiveStatus(company: Company) {
    const activating = !company.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${company.name}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
        company.isActive = !company.isActive;
        this.updateCompanyStatus(company.id!, company);
      }
    });
  }

  private updateCompanyStatus(id: string, company: Company) {
    this.loading = true;
    this.companyService.updateCompany(id, company).pipe(
      catchError(error => {
        console.error('Error updating company status:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update company status. Please try again.'
        });
        this.loading = false;
        return of(null);
      })
    ).subscribe({
      next: (result) => {
        if (result) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Company status updated successfully.'
          });
          this.loadCompanies();
        }
      }
    });
  }

  deleteCompany(company: Company) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${company.name}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.loading = true;
        this.companyService.deleteCompany(company.id!).pipe(
          catchError(error => {
            console.error('Error deleting company:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete company. Please try again.'
            });
            this.loading = false;
            return of(null);
          })
        ).subscribe({
          next: (result) => {
            if (result !== null) {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Company deleted successfully.'
              });
              this.loadCompanies();
            }
          }
        });
      }
    });
  }

  hideDialog() {
    this.displayDialog = false;
    this.fieldTouched = {};
    this.selectedCompany = this.getEmptyCompany();
  }

  resetCompanyForm() {
    this.selectedCompany = this.getEmptyCompany();
    this.fieldTouched = {};
  }

  exportToExcel(): void {
    if (!this.filteredCompanies?.length) {
      this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'No companies to export' });
      return;
    }
    try {
      const exportData = this.filteredCompanies.map(c => ({
        'Name': c.name || '',
        'Status': c.isActive ? 'Active' : 'Inactive'
      }));
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      worksheet['!cols'] = [{ wch: 28 }, { wch: 10 }];
      const workbook: XLSX.WorkBook = { Sheets: { 'Companies': worksheet }, SheetNames: ['Companies'] };
      const excelBuffer: unknown = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, `Companies_${this.formatDateForFile(new Date())}.xlsx`);
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Companies exported to Excel successfully' });
    } catch (error) {
      console.error('Error exporting companies:', error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to export companies to Excel' });
    }
  }

  private formatDateForFile(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  }

  getEmptyCompany(): Company {
    return {
      name: '',
      isActive: true
    };
  }
}